<template>
  <div
    id="promo-code"
    class="mt-3"
    v-if="promoCode">
    <form
      id="promo-code-form"
      name="promoCodeForm"
      @submit.prevent="submitPromoCode(promoCode)"
      novalidate>
      <!-- Page Header -->
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <div class="d-flex flex-column flex-sm-row align-items-center">
              <h3 class="mb-0 mr-3 text-center text-sm-left">
                Promo Code: {{event.name}}
              </h3>
            </div>
            <hr>
          </div>
        </div>
      </div>
      <!-- / Page Header -->

      <div class="container">
        <!-- All fields required -->
        <div class="row">
          <div class="col"><small class="text-danger font-italic">* Required fields</small></div>
        </div>
        <!-- / All fields required -->

        <!-- Number Of Promo Codes / Price -->
        <div class="row mt-3">
          <!-- Number Of Promo Codes -->
          <div
            v-if="!promoCode.id"
            class="col-sm">
            <label for="promo-code-num">
              Number Of Promo Codes: <span class="text-danger font-italic">*</span>
            </label>
            <Number
              id="promo-code-num"
              ref="promoCodeNum"
              name="promoCodeNum"
              step="1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter the number of promo codes desired"
              v-model="promoCode.count"
              :readonly="submitting"
            />
          </div>
          <!-- / Number Of Promo Codes -->

          <!-- Price -->
          <div
            class="col-sm"
            :class="{ 'mt-3 mt-sm-0': !promoCode.id }">
            <label for="promo-code-price">
              Price: <span class="text-danger font-italic">*</span>
            </label>
            <Number
              id="promo-code-price"
              ref="promoCodePrice"
              name="promoCodePrice"
              type="currency"
              step="10"
              placeholder="Enter a price"
              v-model="promoCode.price"
              :readonly="submitting"
            />
          </div>
          <!-- / Price -->

          <!-- Code -->
          <div
            v-if="promoCode.id"
            class="col-sm mt-3 mt-sm-0">
            <label for="promo-code-code">
              Code: <span class="text-danger font-italic">*</span>
            </label>
            <b-form-input
              id="promo-code-code"
              ref="promoCodeCode"
              name="promoCodeCode"
              type="text"
              v-model="promoCode.code"
              readonly
              v-select-on-focus>
            </b-form-input>
          </div>
          <!-- / Code -->
        </div>
        <!-- / Number Of Promo Codes / Price -->

        <!-- Org / KJK Fees -->
        <div class="row mt-3">
          <!-- Org Fee Flat -->
          <div class="col-sm">
            <label for="promo-code-org-fee-flat">
             {{this.loggedInUser.actingOrg.name}} Flat Fee: <span class="text-danger font-italic">*</span>
            </label>
            <Number
              id="promo-code-org-fee-flat"
              ref="promoCodeOrgFeeFlat"
              name="promoCodeOrgFeeFlat"
              type="currency"
              step="10"
              placeholder="Enter a flat fee"
              v-model="promoCode.fees_flat"
              :readonly="submitting"
            />
          </div>
          <!-- / Org Fee Flat -->

          <!-- KJK Fee Flat -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="promo-code-kjk-fee-flat">
             KJK Flat Fee: <span class="text-danger font-italic">*</span>
            </label>
            <Number
              id="promo-code-kjk-fee-flat"
              ref="promoCodeKJKFeeFlat"
              name="promoCodeKJKFeeFlat"
              type="currency"
              step="10"
              placeholder="Enter a flat fee"
              v-model="promoCode.kjk_fees_flat"
              :readonly="submitting"
            />
          </div>
          <!-- / KJK Fee Flat -->
        </div>
        <!-- / Org / KJK Fees -->

        <!-- Valid Start / End -->
        <div class="row mt-3">
          <!-- Start -->
          <div class="col-sm">
            <label for="promo-code-vaild-start">Valid From: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="promo-code-vaild-start"
              ref="promoCodeValidStart"
              name="promoCodeValidStart"
              v-model="promoCode.valid_start_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Start -->

          <!-- End -->
          <div class="col-sm mt-3 mt-sm-0">
            <label for="promo-code-vaild-end">Valid To: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="promo-code-vaild-end"
              ref="promoCodeValidEnd"
              name="promoCodeValidEnd"
              v-model="promoCode.valid_end_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / End -->
        </div>
        <!-- / Valid Start / End -->

        <!-- Notes -->
        <div
          class="row mt-3">
          <!-- Notes -->
          <div class="col-sm-6">
            <label for="promo-code-notes">Notes:</label>
            <b-form-textarea
              id="promo-code-notes"
              ref="promoCodeNotes"
              name="promoCodeNotes"
              rows="6"
              placeholder="Enter promo code notes"
              v-model="promoCode.notes"
              :readonly="submitting"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Notes -->
        </div>
        <!-- / Notes -->

        <!-- Used / Revoked -->
        <div
          v-if="promoCode.id"
          class="row mt-3">
          <!-- Used -->
          <div class="col-sm">
            <b-form-checkbox
              id="promo-code-used"
              class="mt-sm-2"
              name="promoCodeUsed"
              ref="promoCodeUsed"
              v-model="promoCode.used"
              :readonly="submitting">
              Mark Promo Code As Used
            </b-form-checkbox>
          </div>
          <!-- / Used -->

          <!-- Revoked -->
          <div class="col-sm mt-3 mt-sm-0">
            <b-form-checkbox
              id="promo-code-revoked"
              class="mt-sm-2"
              name="promoCodeRevoked"
              ref="promoCodeRevoked"
              v-model="promoCode.revoked"
              :readonly="submitting">
              Mark Promo Code As Revoked
            </b-form-checkbox>
          </div>
          <!-- / Revoked -->
        </div>
        <!-- / Used / Revoked -->

        <div class="corner-buttons">
          <!-- Save Promo Code Button -->
          <b-button
            class="shadow"
            type="submit"
            variant="success"
            :disabled="submitting">
            <font-awesome-icon icon="save"></font-awesome-icon> Save
          </b-button>
          <!-- / Save Promo Code Button -->

          <!-- Delete Promo Code Button -->
          <b-button
            class="shadow ml-3"
            type="button"
            variant="danger"
            v-if="promoCode.id"
            :disabled="submitting"
            @click="submitPromoCode(promoCode.id)">
            <font-awesome-icon icon="trash"></font-awesome-icon> Delete Promo Code
          </b-button>
          <!-- / Delete Promo Code Button -->
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';
import EventBus from '../services/event-bus';

export default {
  name: 'promo-code',
  mixins: [
    DirtyForm,
    AppMixin,
  ],
  data() {
    return {
      promoCode: false,
      promoCodeCopy: false,
      event: {},
      submitting: false,
    };
  },
  computed: {
    promoCodeDirty() {
      const promoCodeJSON = JSON.stringify(this.promoCode);
      const promoCodeCopyJSON = JSON.stringify(this.promoCodeCopy);
      return promoCodeJSON !== promoCodeCopyJSON;
    },
    anyDirty() {
      return this.promoCodeDirty;
    },
  },
  methods: {
    submitPromoCode(promoCode) {
      let promoCodePromise;

      this.submitting = true;

      if (typeof promoCode !== 'object') {
        promoCodePromise = this.deletePromoCode(promoCode);
      } else if (promoCode.id) {
        promoCodePromise = this.updatePromoCode(promoCode);
      } else {
        promoCodePromise = this.createPromoCode(promoCode);
      }

      promoCodePromise
        .then(() => {
          this.$router.push({
            name: 'event',
            params: {
              eventID: this.event.id,
              tab: 'pricing',
            },
          });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return promoCodePromise;
    },
    createPromoCode(promoCode) {
      this.showToast(`Creating promo code${promoCode.cound > 1 ? 's' : ''}...`);

      return DataSvc.createPromoCode(this.event.id, promoCode)
        .then((p) => {
          this.promoCode = p;
          this.promoCodeCopy = _.cloneDeep(p);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Promo code created!');
        });
    },
    updatePromoCode(promoCode) {
      this.showToast('Updating promo code...');

      return DataSvc.updatePromoCode(promoCode)
        .then((p) => {
          this.promoCode = p;
          this.promoCodeCopy = _.cloneDeep(p);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Promo code updated!');
        });
    },
    deletePromoCode(promoCodeID) {
      const confirmDelete = () => {
        this.showToast('Deleting promo code...');

        return DataSvc.deletePromoCode(promoCodeID)
          .then(() => this.delay(1000))
          .then(() => {
            this.showToast('Promo code deleted!');
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this promo code?',
        'Deleting a promo code',
      ).then(confirmDelete);
    },
  },
  created() {
    this.showGlobalLoader('Loading promo code...');

    const initPromoCode = () => {
      const { eventID, promoCodeID } = this.$route.params;
      let promoCodePromise;

      // get event
      const eventPromise = DataSvc.getEvent(eventID)
        .then((event) => {
          this.event = event;

          if (this.loggedInUser.actingOrg.id !== event.org_id) {
            this.findAndSwitchActingOrg(event.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get promo code
      if (promoCodeID === 'new') {
        promoCodePromise = Promise.resolve({});
      } else {
        promoCodePromise = DataSvc.getPromoCode(promoCodeID);
      }

      promoCodePromise
        .catch(() => {
          this.showWarning('That promo code does not exist.');
          this.$router.replace({ name: 'events' });
        })
        .then((promoCode) => {
          this.promoCode = promoCode;
          this.promoCodeCopy = _.cloneDeep(promoCode);
        });

      return Promise.all([eventPromise, promoCodePromise]);
    };

    this.initPromise.then(initPromoCode).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss"></style>
